import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://www.kapwing.com/pricing";
const REPURPOSE = "https://www.kapwing.com/tools/repurpose";
const SPEAKER_FOCUS = "https://www.kapwing.com/ai/auto-speaker-focus";
const WATERMARK = "https://www.kapwing.com/help/our-watermark-policy/";
const CHECKED = "2026-10-08";

export const KAPWING: CompetitorRecord = {
  slug: "kapwing",
  name: "Kapwing",
  aliases: ["Kapwing.com", "Kapwing AI", "Kapwing Repurpose Studio"],
  url: "https://www.kapwing.com",
  pricingUrl: PRICING,
  category: "editor",
  positioning: "Browser-based video editor for teams, from memes to marketing, with a Repurpose Studio for clips.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "10 credits per month and 10 minutes of subtitling",
      includes: [
        "Watermark on all exports",
        "720p export",
        "4-minute export length, 30 minutes of export a month",
        "2 GB upload, files kept 3 days",
        "Projects unlisted, not private",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "The watermark policy page confirms the watermark applies to every free project.",
    },
    {
      name: "Pro",
      monthly: 24,
      annualPerMonth: 16,
      annualTotal: 192,
      metered: "1,000 credits per member per month, 1,000 subtitle minutes",
      includes: [
        "Priced per member",
        "4K export, 120-minute exports",
        "50 minutes of text to speech and 50 minutes of dubbing",
        "6 GB upload, 200 GB storage",
        "Brand kit",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Business",
      monthly: 64,
      annualPerMonth: 50,
      annualTotal: 600,
      metered: "4,000 credits per member per month, extra credits for sale",
      includes: [
        "Priced per member",
        "800 GB storage",
        "Voice clones and lip sync",
        "Everything in Pro",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Enterprise",
      custom: true,
      metered: "Custom credits",
      includes: ["SAML SSO", "Custom contract"],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
  ],
  entryPlanName: "Pro",
  entryPlanIncludes: "1,000 credits per member, 1,000 subtitle minutes, 4K export, 6 GB upload, 200 GB storage",
  freeTier: "10 credits a month at 720p with a watermark on every export, 4-minute exports, 30 export minutes a month, files kept 3 days",
  trial: "None for paid plans",
  meteringUnit: "Credits per member per month, spent per AI task; the cost of one Repurpose clip is not published",
  exportResolution: "720p on Free, 4K on Pro and above",
  features: {
    longToShort: { v: "yes", note: "Repurpose Studio, prompt-steered clips", source: REPURPOSE },
    momentRanking: { v: "no", note: "No virality score" },
    reframing: { v: "yes", note: "Auto Speaker Focus centers the active speaker", source: SPEAKER_FOCUS },
    maxSource: { v: "text", note: "3 hours in Repurpose Studio; 2 GB Free, 6 GB Pro", source: REPURPOSE },
    layouts: { v: "n/a", note: "Not enumerated on the Repurpose page" },
    redditStory: { v: "no" },
    fakeText: { v: "no" },
    aiImages: { v: "yes" },
    voiceover: { v: "yes", note: "50 text-to-speech minutes a month on Pro" },
    voiceCloning: { v: "partial", note: "Business plan only" },
    avatars: { v: "n/a", note: "Lip sync on Business; avatars not stated" },
    timelineEditor: { v: "yes", note: "On Free too, with a watermark" },
    captionStyles: { v: "n/a", note: "Several styles, no count published" },
    captionLanguages: { v: "text", note: "100+" },
    bRoll: { v: "yes", note: "B-roll generator" },
    fourK: { v: "yes", note: "Pro and above" },
    scheduling: { v: "no" },
    analytics: { v: "no" },
    socialTracker: { v: "no" },
    api: { v: "no", note: "No public API" },
    webhooks: { v: "no", note: "No public API" },
    web: { v: "yes" },
    desktop: { v: "no" },
    mobile: { v: "no", note: "Not advertised" },
    teamSeats: { v: "yes", note: "Priced per member on Pro and Business" },
    freeTier: { v: "yes", note: "720p, watermark, 4-minute exports" },
    trial: { v: "no", note: "No trial for paid plans" },
    metering: { v: "text", note: "Credits per AI task, per member" },
    rollover: { v: "n/a", note: "Not stated on the pricing page" },
    refunds: { v: "text", note: "No refunds once you upgrade" },
    cancelInApp: { v: "n/a", note: "Not stated on the pricing page" },
  },
  billingTerms: {
    refunds: "The pricing page states that refunds are not issued once you upgrade, as of Oct 8, 2026.",
    cancel: "Cancellation steps are not described on the pricing page.",
    sourceUrl: PRICING,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "Refused to refund a service I never used",
      date: "2026-09-03",
      url: "https://www.trustpilot.com/review/kapwing.com",
      evidences: "refunds",
    },
    {
      text: "eating tons of credits",
      date: "2026-07-21",
      url: "https://www.trustpilot.com/review/kapwing.com",
      evidences: "metering",
    },
  ],
  verification: {
    method: "fetch",
    date: CHECKED,
    note: `Pricing page fetched directly: Free $0, Pro $24 per member monthly or $16 per member annual ($192 a year), Business $64 per member monthly or $50 annual ($600 a year), Enterprise custom. The watermark policy (${WATERMARK}) and the Repurpose and Auto Speaker Focus pages were fetched too. One claim is third-party and not on a Kapwing page: costbench.com says all AI features freeze when credits are depleted; it is cited as third-party in prose and should be browser-checked against the Kapwing help center before it is stated as Kapwing's own policy.`,
  },
  seoPattern: "kapwing.com/resources/kapwing-vs-<competitor>-comparison... blog posts, no /vs or /compare directory, no page about Crayo",
};
